import * as React from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

/**
 * Splash Screen (Void) - Design System Atmosférico v3.0
 * 
 * Specs:
 * - 2s de void absoluto antes do logo
 * - Logo com aura-breathe
 * - Fade out suave para Dashboard
 * 
 * @see decreto v3.0 - Tela Void
 */
export default function Splash() {
    const navigate = useNavigate();
    const [phase, setPhase] = React.useState<"void" | "logo" | "exit">("void");

    React.useEffect(() => {
        // Phase 1: Pure void (1s)
        const voidTimer = setTimeout(() => {
            setPhase("logo");
        }, 1000);

        // Phase 2: Logo breathing (2s)
        const logoTimer = setTimeout(() => {
            setPhase("exit");
        }, 3000);

        // Phase 3: Navigate to dashboard
        const exitTimer = setTimeout(() => {
            navigate("/dashboard", { replace: true });
        }, 3500);

        return () => {
            clearTimeout(voidTimer);
            clearTimeout(logoTimer);
            clearTimeout(exitTimer);
        };
    }, [navigate]);

    return (
        <div
            className={cn(
                "fixed inset-0 bg-void-absolute flex items-center justify-center",
                "transition-opacity duration-flow",
                phase === "exit" && "opacity-0"
            )}
        >
            {/* Logo Container */}
            <div
                className={cn(
                    "flex flex-col items-center gap-4",
                    "transition-all duration-dramatic ease-decelerate",
                    phase === "void" ? "opacity-0 scale-95" : "opacity-100 scale-100"
                )}
            >
                {/* Logo Mark */}
                <div
                    className={cn(
                        "relative w-20 h-20 rounded-bold",
                        "bg-gradient-to-br from-gold-primary to-gold-soft",
                        "shadow-gold-glow",
                        phase === "logo" && "animate-aura-breathe"
                    )}
                >
                    {/* P Letter */}
                    <span
                        className="absolute inset-0 flex items-center justify-center text-void-absolute text-display font-display"
                    >
                        P
                    </span>
                </div>

                {/* Wordmark */}
                <h1
                    className={cn(
                        "text-headline text-gradient-gold font-display tracking-wider",
                        "transition-opacity duration-flow delay-200",
                        phase === "void" ? "opacity-0" : "opacity-100"
                    )}
                >
                    PUBLEE
                </h1>

                {/* Tagline */}
                <p
                    className={cn(
                        "text-caption text-textSecondary",
                        "transition-opacity duration-flow delay-500",
                        phase === "void" ? "opacity-0" : "opacity-100"
                    )}
                >
                    Conteúdo que vende
                </p>
            </div>

            {/* Atmospheric gradient at bottom */}
            <div
                className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
                style={{
                    background: "linear-gradient(0deg, rgba(255,215,0,0.05) 0%, transparent 100%)"
                }}
            />
        </div>
    );
}
